import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { getSearchQueries } from "@/lib/seo/gsc.functions";
import { getIntentReport } from "@/lib/analytics/intents.functions";

export const Route = createFileRoute("/_authenticated/admin/sokfragor")({
  head: () => ({
    meta: [
      { title: "Sökfrågor från Google — Fenny admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: QueriesPage,
});

const nf = new Intl.NumberFormat("sv-SE");
const pct = (v: number) => `${v.toFixed(1)}%`;

function QueriesPage() {
  const [site, setSite] = useState<string | undefined>(undefined);
  const [days, setDays] = useState(28);
  const fn = useServerFn(getSearchQueries);
  const intentFn = useServerFn(getIntentReport);

  const q = useQuery({
    queryKey: ["admin", "gsc", site, days],
    queryFn: () => fn({ data: { days, siteUrl: site, refresh: false } }),
  });
  const intents = useQuery({
    queryKey: ["admin", "intents", days],
    queryFn: () => intentFn({ data: { days } }),
  });

  const report = q.data;

  const comparison = useMemo(() => {
    if (!report || report.status !== "ok") return [];
    const site = new Map(intents.data?.rows.map((r) => [r.id, r]) ?? []);
    const ids = new Set([...report.intents.map((i) => i.id), ...(intents.data?.rows.map((r) => r.id) ?? [])]);
    return [...ids]
      .map((id) => {
        const g = report.intents.find((i) => i.id === id);
        const s = site.get(id);
        return {
          id,
          label: g?.label ?? s?.label ?? id,
          clicks: g?.clicks ?? 0,
          impressions: g?.impressions ?? 0,
          position: g?.position ?? 0,
          visitors: s?.visitors ?? 0,
          leads: s?.leads ?? 0,
          conversion: s && s.visitors > 0 ? (s.leads / s.visitors) * 100 : null,
          note:
            (g?.clicks ?? 0) > 0 && (s?.visitors ?? 0) === 0
              ? ("saknas" as const)
              : (g?.impressions ?? 0) > 100 && (g?.clicks ?? 0) === 0
                ? ("syns" as const)
                : null,
        };
      })
      .sort((a, b) => b.impressions - a.impressions || b.visitors - a.visitors);
  }, [report, intents.data]);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Sökfrågor från Google</h1>
        <p className="text-sm text-white/60 max-w-2xl">
          Vilka frågor människor söker på innan de landar hos oss — och hur de grupperas mot samma
          sökintentioner som mätningen på sajten.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {[7, 28, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-full px-3 py-1 text-xs ${
                days === d ? "bg-white/15 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
              }`}
            >
              {d} dagar
            </button>
          ))}
        </div>
      </header>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Hämtar sökfrågor…
        </div>
      )}
      {q.isError && (
        <p className="text-sm text-red-400">
          Kunde inte hämta sökfrågorna just nu. Försök igen om en stund.
        </p>
      )}

      {report?.status === "not_connected" && (
        <p className="text-sm text-amber-300">{report.message}</p>
      )}

      {report?.status === "selection_required" && (
        <div className="space-y-2">
          <p className="text-sm text-white/70">Välj vilken Google-egendom vi ska läsa data från:</p>
          <div className="flex flex-wrap gap-2">
            {report.candidates.map((c) => (
              <button
                key={c}
                onClick={() => setSite(c)}
                className="rounded-full bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {report?.status === "ok" && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs text-white/50">
            <span>
              {report.period_start} – {report.period_end} · {report.site_url}
            </span>
            <button
              onClick={() => q.refetch()}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 hover:bg-white/10"
            >
              <RefreshCw className="h-3 w-3" /> Uppdatera
            </button>
            {report.cached && <span>Sparad kopia, uppdateras dagligen</span>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Klick" value={nf.format(report.totals.clicks)} />
            <Kpi label="Visningar" value={nf.format(report.totals.impressions)} />
            <Kpi label="Klickandel" value={pct(report.totals.ctr)} />
            <Kpi label="Snittposition" value={report.totals.position.toFixed(1)} />
          </div>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-white/80">Google mot sajtens mätning</h2>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-white/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Sökintention</th>
                    <th className="px-4 py-3 text-right font-medium">Klick i Google</th>
                    <th className="px-4 py-3 text-right font-medium hidden md:table-cell">Visningar</th>
                    <th className="px-4 py-3 text-right font-medium hidden md:table-cell">Position</th>
                    <th className="px-4 py-3 text-right font-medium">Besökare</th>
                    <th className="px-4 py-3 text-right font-medium">Hör av sig</th>
                    <th className="px-4 py-3 text-right font-medium">Andel</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row) => (
                    <tr key={row.id} className="border-t border-white/10">
                      <td className="px-4 py-3">
                        {row.label}
                        {row.note === "saknas" && (
                          <span className="ml-2 rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] text-amber-300">
                            klick utan besök
                          </span>
                        )}
                        {row.note === "syns" && (
                          <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/60">
                            syns men klickas inte
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{nf.format(row.clicks)}</td>
                      <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-white/70">
                        {nf.format(row.impressions)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-white/70">
                        {row.position ? row.position.toFixed(1) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{nf.format(row.visitors)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{nf.format(row.leads)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.conversion == null ? "—" : pct(row.conversion)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-white/40">
              Klick kommer från Google, besökare och förfrågningar från sajtens egen mätning. Stora
              skillnader betyder att trafiken finns men inte fastnar — eller tvärtom.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-white/80">Sökfrågor</h2>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-white/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Sökfråga</th>
                    <th className="px-4 py-3 text-left font-medium hidden md:table-cell">Landar på</th>
                    <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">Intention</th>
                    <th className="px-4 py-3 text-right font-medium">Klick</th>
                    <th className="px-4 py-3 text-right font-medium">Visningar</th>
                    <th className="px-4 py-3 text-right font-medium hidden md:table-cell">CTR</th>
                    <th className="px-4 py-3 text-right font-medium hidden md:table-cell">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={`${r.query}-${r.page}-${i}`} className="border-t border-white/10">
                      <td className="px-4 py-2.5">{r.query}</td>
                      <td className="px-4 py-2.5 text-white/60 hidden md:table-cell">
                        {r.page ? r.page.replace("https://fenny.se", "") : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-white/60 hidden lg:table-cell">{r.intent_label}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{nf.format(r.clicks)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-white/70">
                        {nf.format(r.impressions)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums hidden md:table-cell text-white/70">
                        {pct(r.ctr)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums hidden md:table-cell text-white/70">
                        {r.position.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-xs text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
